import {
  Button,
  CodeHighlight,
  Heading,
  SlideOverPanel,
  stringify,
  useCopyToClipboard,
  useCurrentOrPrevious,
  useDetectTheme,
} from '@portal/components'
import classNames from 'classnames'
import { useAppContext } from 'context'
import dayjs from 'dayjs'
import upperFirst from 'lodash/upperFirst'
import { Check, Copy, X } from 'react-feather'

import type { ApiKey } from './ApiKeyManager'

// read/write type def is the same, so I am just using read for this type alias
type PermissionRule = ApiKey['permission']['read']

type Props = {
  apiKey?: ApiKey
  onClose(): void
}

export default function ApiKeyInspector({ apiKey, onClose }: Props) {
  const { isSuperAdmin } = useAppContext()

  const displayedApiKey = useCurrentOrPrevious(apiKey)

  const getFriendlyAccessName = (access: PermissionRule) => {
    if (access.everything) {
      return 'Full'
    } else if (Object.keys(access.queriesByCollection ?? {}).length > 0) {
      return 'Custom'
    } else {
      return 'Disabled'
    }
  }

  const renderInfo = () => {
    if (!displayedApiKey) {
      return null
    }

    const isExpired = dayjs(displayedApiKey.expiresAt).isBefore(dayjs())

    return (
      <div>
        <InfoLine label="Description" value={displayedApiKey.description} />
        <InfoLine
          label="Created"
          value={dayjs(displayedApiKey.createdAt).format('LL')}
          isContrast
        />
        <InfoLine
          label={isExpired ? 'Expired' : 'Expires'}
          value={dayjs(displayedApiKey.expiresAt).format('LL')}
        />
        {isSuperAdmin && (
          <InfoLine
            label="Remote query"
            value={
              displayedApiKey.permission.remoteQuery ? 'Enabled' : 'Disabled'
            }
            isContrast
          />
        )}
        <InfoLine
          label="Read access"
          value={getFriendlyAccessName(displayedApiKey.permission.read)}
        />
        <InfoLine
          label="Write access"
          value={getFriendlyAccessName(displayedApiKey.permission.write)}
        />
      </div>
    )
  }

  return (
    <SlideOverPanel
      isOpen={!!apiKey}
      size="xl"
      className="border-border-normal border-l bg-background-overlay shadow"
      wrapperClassName="fixed"
    >
      <div className="border-b-border-normal flex h-12 w-full items-center border-b">
        <Heading level={3} className="flex-1 px-4">
          API key details
        </Heading>

        <div className="flex items-center h-12 w-12">
          <Button
            size="icon"
            className="flex h-8 w-8 items-center justify-center border-none hover:bg-background-surface/70 active:bg-background-surface/80"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {renderInfo()}

        {displayedApiKey && (
          <>
            <ApiKeyPermissionJSON
              kind="read"
              rule={displayedApiKey.permission.read}
            />
            <ApiKeyPermissionJSON
              kind="write"
              rule={displayedApiKey.permission.write}
            />
          </>
        )}
      </div>
    </SlideOverPanel>
  )
}

type InfoLineProps = {
  label: string
  value: string
  isContrast?: boolean
}

function InfoLine({ label, value, isContrast }: InfoLineProps) {
  return (
    <div
      data-testid={`info-line-${label}-container`}
      className={classNames('flex h-10 items-center justify-between px-4', {
        'bg-background-overlay-hovered': isContrast,
      })}
    >
      <Heading
        level={4}
        data-testid={`info-line-${label}-label`}
        className="text-base"
      >
        {label}
      </Heading>

      <p
        className="text-base text-foreground-normal"
        data-testid={`info-line-${label}-value`}
      >
        {value}
      </p>
    </div>
  )
}

type ApiKeyPermissionProps = {
  kind: 'read' | 'write'
  rule: PermissionRule
}

function ApiKeyPermissionJSON({ kind, rule }: ApiKeyPermissionProps) {
  const theme = useDetectTheme()

  const jsonString = stringify(rule.queriesByCollection, null, 2) || ''
  const [performCopy, recentlyCopied, canCopy] = useCopyToClipboard(jsonString)

  if (rule.everything || Object.keys(rule.queriesByCollection).length === 0) {
    return null
  }

  return (
    <div>
      <div className="flex items-center justify-between bg-background-overlay-hovered p-2">
        <Heading level={4} className="text-base">
          {upperFirst(kind)} queries by collection
        </Heading>

        {canCopy && (
          <span className="flex items-center whitespace-nowrap text-sm text-foreground-normal">
            <Button size="icon" onClick={performCopy}>
              {recentlyCopied ? (
                <Check className="ml-1 h-4 w-4 text-fill-success" />
              ) : (
                <Copy className="ml-1 h-4 w-4 text-foreground-subtle text-opacity-80 hover:text-opacity-100" />
              )}
            </Button>
          </span>
        )}
      </div>

      <CodeHighlight
        code={jsonString}
        language="json"
        theme={theme === 'dark' ? 'dittoDark' : 'dittoLight'}
        lineNumbers
        className="font-plex-mono"
      />
    </div>
  )
}
